import EzController from 'controllers/ez-base-controller';
import { ezResourceGrid } from 'ez-styles/assets/javascripts/ez-styles/justified-grid';
import {
  triggerCustomEvent,
  splitTestEnabled,
  truncateThumbnailTitles,
  truncateText,
  featureEnabled,
  parseTemplate,
  createElementFromHTML,
} from 'lib/helpers'; // split-test: resource_show_modal_view
import { getAjaxModal, ajaxPost, ajaxGet } from 'lib/ajax-helpers'; // split-test: resource_show_modal_view
import tippy from 'tippy.js'; // eslint-disable-line import/no-extraneous-dependencies

import events from 'lib/events'; // split-test: resource_show_modal_view

require('intersection-observer');

export default class ResourcesGridController extends EzController {
  defaultFavoritedTemplate = `<a data-target="unfavorite-resource" class="ez-btn--light
                                          resource-action-button
                                          resource-action-button--favorite
                                          resource-action-button--square
                                          is-favorited" title="Remove from favorites" 
                              id="{{item_id}}" data-remote="true" 
                              rel="nofollow" data-method="delete" 
                              href="/vector-art/{{item_id}}-{{item_handle}}/favorite">
                            <svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" data-name="Layer 1" viewBox="0 0 15.92 13.63"><g id="Page-1"><g id="Design-Guide"><path id="Heart-Fill" d="M8,13.63a1.43,1.43,0,0,1-.79-.24l0,0C2.79,10.49.4,7.7.05,5.08A4.64,4.64,0,0,1,1.16,1.33,4.07,4.07,0,0,1,4.69,0,4.87,4.87,0,0,1,8,2a4.82,4.82,0,0,1,3.25-2,4.08,4.08,0,0,1,3.55,1.3,4.65,4.65,0,0,1,1.11,3.73c-.35,2.62-2.74,5.41-7.08,8.3l0,0a1.46,1.46,0,0,1-.81.25" transform="translate(0 0)"></path></g></g></svg>
                          </a>
                          `;

  connect() {
    this.initGrid();

    window.addEventListener('InitAllGrids', this.initGrid);
    window.addEventListener('TruncateGridTitles', this.truncateTitles);
    window.addEventListener('lazyPartnerGrids', this.lazyPartnerGrids);

    if (this.element.parentNode.classList.contains('ez-search-results')) {
      this.insertSearchDeboostingButtons();
    }

    if (featureEnabled('thumbnail-contributor-info')) {
      this.setupContribInfoTooltips();
    }
  }

  disconnect() {
    window.removeEventListener('InitAllGrids', this.initGrid);
    window.removeEventListener('TruncateGridTitles', this.truncateTitles);
    window.removeEventListener('lazyPartnerGrids', this.lazyPartnerGrids);
  }

  setupContribInfoTooltips() {
    const controller = this;

    tippy('[data-target="resources-grid.nameTarget"]', {
      content(reference) {
        const parentLi = controller.getParentLi(reference);
        const contribInfo = parentLi.querySelector('[data-target="resources-grid.contribInfo"]');

        return contribInfo.innerHTML;
      },
      allowHTML: true,
      animation: 'rising',
      interactive: true,
      placement: 'bottom-start',
      theme: 'contrib-info',
      delay: 250,
      onShow(instance) {
        controller.tooltipParentLi = controller.getParentLi(instance.reference);

        controller.tooltipParentLi.classList.add('is-active');

        setTimeout(() => { // wait just a tick
          truncateText(controller.tooltipParentLi, '.ez-resource-grid__item__contrib-info__username', 1, false);
        });

        controller.tooltipProfileInfo = instance.popper.querySelector('.contrib-info-theme .ez-resource-grid__item__contrib-info__header');
        controller.tooltipProfileInfo.addEventListener('click', controller.clickThroughToProfile);
      },
      onHide() {
        controller.tooltipParentLi.classList.remove('is-active');

        controller.tooltipProfileInfo.removeEventListener('click', controller.clickThroughToProfile);
      },
    });
  }

  clickThroughToResource = (event) => {
    const parentLi = this.getParentLi(event.currentTarget);

    parentLi.querySelector('.ez-resource-thumb__link').click();
  }

  clickThroughToProfile = (event) => {
    const profileBtn = event.currentTarget.querySelector('.ez-resource-grid__item__contrib-info__profile-btn');

    triggerCustomEvent(events.resources.grid.contributor_info_profile_click);

    profileBtn.click();
  }

  getParentLi(elem) {
    return elem.closest('.ez-resource-grid__item');
  }

  remove = () => {
    this.element.remove();
  }

  initGrid = () => {
    if (this.element.classList.contains('ez-resource-grid--partner')) {
      return;
    }

    ezResourceGrid(this.element, {
      rowHeight: parseInt(this.element.getAttribute('data-row-height'), 10),
      maxRows: parseInt(this.element.getAttribute('data-max-rows'), 10),
      truncate: !!this.element.getAttribute('data-truncate-results'),
    });

    const gridItems = this.element.querySelectorAll('.ez-resource-grid__item');
    gridItems.forEach((item, index) => {
      item.setAttribute('data-position', index + 1);
    });

    if (this.data.get('lazy') === 'true') {
      this.loadLazy(this.element.getAttribute('data-lazy-offset'));
    }

    if (this.hasFavorites()) {
      this.loadFavoritedHtml();
    }
  }

  activateFavorited() {
    this.favoritedIds.forEach((item) => {
      item.item_id = item.resource_id;

      const favoritedItem = this.element.querySelector(`[data-item-id="${item.item_id}"]`);

      if (favoritedItem) {
        const favElem = favoritedItem.querySelector('[data-controller="resource-favorite"]');

        item.favorite_path = favElem.href;

        const faveHTML = createElementFromHTML(parseTemplate(this.favoritedHtml, item));

        favElem.parentNode.replaceChild(faveHTML, favElem);
      }
    });
  }

  loadFavoritedHtml() {
    ajaxGet('/favorites/async_favorited_template', (response) => {
      this.favoritedHtml = response.data || this.defaultFavoritedTemplate;

      this.activateFavorited();
    });
  }

  hasFavorites() {
    return this.element.hasAttribute('data-favorites') && this.favoritedIds.length > 0;
  }

  get favoritedIds() {
    return JSON.parse(this.element.getAttribute('data-favorites'));
  }

  truncateTitles = () => {
    truncateThumbnailTitles(this.element);
  }

  lazyPartnerGrids = (event) => {
    if (this.element === event.detail.element) {
      this.loadLazy(this.element.getAttribute('data-lazy-offset'));
    }
  }

  // start split-test: resource_show_modal_view
  // can remove check for split test and else path
  // if split test wins
  loadShowPage = (event) => {
    if (splitTestEnabled('show-page-modal-view')) {
      event.preventDefault();

      const resourceShowUrl = event.currentTarget.getAttribute('href');

      getAjaxModal(resourceShowUrl);

      triggerCustomEvent(events.show_page.modal.build_resource_navigation_context, {
        targetResourcePath: resourceShowUrl,
        targetResourceGroup: event.currentTarget.closest('[data-controller="resources-grid"]'),
      });
    }
  }
  // end split-test: resource_show_modal_view

  loadLazy(lazyLoadOffset) {
    const lazyImages = [].slice.call(this.element.querySelectorAll('.ez-resource-thumb__img'));

    if ('IntersectionObserver' in window) {
      const lazyImageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const lazyImage = entry.target;
            lazyImage.src = lazyImage.dataset.lazySrc;
            lazyImage.srcset = lazyImage.dataset.lazySrcset;
            lazyImageObserver.unobserve(lazyImage);
          }
        });
      }, { rootMargin: `0px 0px ${lazyLoadOffset}px 0px` });

      lazyImages.forEach((lazyImage) => {
        lazyImageObserver.observe(lazyImage);
      });
    } else { // We should not get here as we use polyfill
      console.error('Intersection observer not supported'); // eslint-disable-line no-console
    }
  }

  // quick and dirty for Shawn, refactor me
  insertSearchDeboostingButtons() {
    if (this.showDeboost) {
      const allResultsGridItems = document.querySelectorAll('.ez-search-results .ez-resource-thumb__link');
      allResultsGridItems.forEach((item) => {
        const nopeButton = document.createElement('DIV');

        nopeButton.classList.add('ez-resource-thumb__link__admin');
        nopeButton.classList.add('ez-resource-thumb__link__admin--nope');
        nopeButton.dataset.action = 'click->resources-grid#nopeResource';

        item.appendChild(nopeButton);
      });

      const grid = document.querySelectorAll('.ez-resource-grid')[0];
      const termButton = document.createElement('a');
      const termLink = document.createTextNode('exclude term');
      termButton.appendChild(termLink);
      termButton.dataset.action = 'click->resources-grid#excludeTerm';
      termButton.href = '#';
      grid.appendChild(termButton);
    }
  }

  get showDeboost() {
    return document.body.classList.contains('is-admin-user')
    && document.body.classList.contains('searches-show')
    && featureEnabled('search-deboost')
    && this.contentType === 'photo';
  }

  nopeResource = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    const ajaxUrl = '/admin/search/search_term_options/exclude';
    ajaxPost(ajaxUrl, this.afterSearchTweak, () => {}, this.stoParams(this.parseResourceId(event)));
  }

  excludeTerm = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    // eslint-disable-next-line no-alert
    const excluded = prompt('enter term to exclude');

    const params = {
      term: this.searchTerm,
      locale: this.locale,
      content_type: this.contentType,
      excluded_term: excluded,
    };
    const ajaxUrl = '/admin/search/search_term_options/exclude_term';
    ajaxPost(ajaxUrl, this.afterSearchTweak, () => {}, params);
  }

  stoParams = (resourceId) => ({
    term: this.searchTerm,
    locale: this.locale,
    content_type: this.contentType,
    resource_id: resourceId,
  })

  afterSearchTweak = () => {
    window.EezyScrollPositionHelper.preservePositionOnce();
    Turbolinks.visit(window.location);
  }

  get searchTerm() {
    const searchInfo = document.querySelector('[data-search-term]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-term');
    }

    return '';
  }

  get locale() {
    return document.documentElement.getAttribute('lang');
  }

  get contentType() {
    return document.querySelector('#search').getAttribute('data-content-type');
  }

  parseResourceId(event) {
    const resourceId = event.target.parentNode.parentNode.dataset.itemId;
    return resourceId;
  }
}
